<!-- biome-ignore-all lint/a11y/noStaticElementInteractions: renders a RouterLink anchor or an `a`, and binds role/tabindex itself when no href makes it natively focusable -->
<script setup lang="ts">
  import { computed, useAttrs, useId } from 'vue';
  import { RouterLink } from 'vue-router';
  import { sanitizeUrl } from '@braintree/sanitize-url';
  import { useLocale } from '@tundr/i18n';
  import FontIcon from '../FontIcon/FontIcon.vue';
  import { visuallyHiddenClass } from '../_shared/styles/visually-hidden.css';
  import {
    baseClass,
    iconLeftClass,
    iconRightClass,
    labelClass,
    variantClasses,
  } from './Link.css';
  import type { LinkEmits, LinkProps, LinkSlots } from './Link.types';
  import { LinkTarget, LinkVariant } from './Link.types';

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<LinkProps>(), {
    disabled: false,
    variant: LinkVariant.Primary,
  });

  const emit = defineEmits<LinkEmits>();

  defineSlots<LinkSlots>();

  const attrs = useAttrs();
  const { t } = useLocale('ds-components');

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-link-${uid}`);

  const isValidUrl = (value: string): boolean => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const sanitizedHref = computed(() => {
    if (props.disabled || !props.href) return undefined;

    return isValidUrl(props.href) ? sanitizeUrl(props.href) : undefined;
  });

  const isRouterLink = computed(() => !!props.to && !props.disabled);

  const isNativelyFocusable = computed(
    () => isRouterLink.value || !!sanitizedHref.value,
  );

  const resolvedTarget = computed(() => {
    if (isRouterLink.value) return props.target;

    return sanitizedHref.value ? (props.target ?? LinkTarget.Blank) : undefined;
  });

  const isBlankTarget = computed(
    () => resolvedTarget.value === LinkTarget.Blank,
  );

  const relValue = computed(() =>
    isBlankTarget.value ? 'noopener noreferrer' : undefined,
  );

  const isActionOnly = computed(
    () => !props.disabled && !isNativelyFocusable.value,
  );

  const onClick = (event: MouseEvent) => {
    if (props.disabled) {
      event.preventDefault();
      return;
    }

    emit('click', event);
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (!isActionOnly.value || event.key !== 'Enter') return;

    event.preventDefault();
    emit('click', event);
  };
</script>

<template>
  <component
    :is="isRouterLink ? RouterLink : 'a'"
    v-bind="attrs"
    :to="isRouterLink ? to : undefined"
    :href="sanitizedHref"
    :target="resolvedTarget"
    :rel="relValue"
    :role="isNativelyFocusable ? undefined : 'link'"
    :tabindex="isActionOnly ? 0 : undefined"
    :aria-disabled="disabled ? 'true' : undefined"
    :data-testid="resolvedTestId"
    :class="[baseClass, variantClasses[variant]]"
    @click="onClick"
    @keydown="onKeydown"
  >
    <FontIcon v-if="iconLeft" :icon-name="iconLeft" :class="iconLeftClass" />
    <span :class="labelClass"><slot>{{ label }}</slot></span>
    <FontIcon v-if="iconRight" :icon-name="iconRight" :class="iconRightClass" />
    <span v-if="isBlankTarget" :class="visuallyHiddenClass">
      {{ t('link.opens_new_window') }}
    </span>
  </component>
</template>
